/**
 * BlissNet theme preference — single source of truth for stored mode + bootstrap.
 * - `system`: follow prefers-color-scheme (live updates)
 * - `dark` / `light`: explicit user choice (Settings toggle / ThemeToggle)
 *
 * Legacy key `blissnet-theme` only stored dark|light; forced-dark era wrote `dark` for everyone.
 * New key `blissnet-theme-mode` stores system|dark|light. On first read we migrate legacy `light`
 * to explicit light; legacy `dark` or missing becomes `system` so the app follows OS again.
 */

export const THEME_MODE_KEY = 'blissnet-theme-mode';
export const LEGACY_THEME_KEY = 'blissnet-theme';

export type ThemeMode = 'system' | 'dark' | 'light';

function prefersDarkScheme(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch {
    return false;
  }
}

export function effectiveIsDark(mode: ThemeMode, prefersDark: boolean): boolean {
  if (mode === 'system') return prefersDark;
  return mode === 'dark';
}

/** Read stored mode and run one-time migration from legacy storage. */
export function readThemeMode(): ThemeMode {
  if (typeof localStorage === 'undefined') return 'system';
  try {
    const v2 = localStorage.getItem(THEME_MODE_KEY);
    if (v2 === 'system' || v2 === 'dark' || v2 === 'light') {
      return v2;
    }

    const legacy = localStorage.getItem(LEGACY_THEME_KEY);
    if (legacy === 'light') {
      localStorage.setItem(THEME_MODE_KEY, 'light');
      return 'light';
    }

    // Legacy `dark` was often forced for all users; missing key = first visit → follow system
    localStorage.setItem(THEME_MODE_KEY, 'system');
    localStorage.removeItem(LEGACY_THEME_KEY);
    return 'system';
  } catch {
    return 'system';
  }
}

export function persistThemeMode(mode: ThemeMode): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(THEME_MODE_KEY, mode);
    if (mode === 'system') {
      localStorage.removeItem(LEGACY_THEME_KEY);
    } else {
      localStorage.setItem(LEGACY_THEME_KEY, mode);
    }
  } catch {
    // ignore quota / private mode
  }
}

/** Used in main.tsx before React — must match ThemeProvider effective theme. */
export function getBootstrapIsDark(): boolean {
  const mode = readThemeMode();
  return effectiveIsDark(mode, prefersDarkScheme());
}
